const TEL_LINK_PATTERN = /\b(0\d{1,2}-\d{3,4}-\d{3,4}|0\d{1,2}-\d{5,8})\b/g;
const EMPTY_PARAGRAPH_PATTERN =
  /<p\b[^>]*>(?:\s|&nbsp;|&#160;|<br\s*\/?>)*<\/p>/gi;
const EMPTY_TAG_WITHOUT_ATTRIBUTES_PATTERN = /<([a-z][^/\s<>]*)>\s*<\/\1>/gi;
const LEADING_BREAKS_PATTERN = /^(?:\s*<br\s*\/?>)+/i;
const LEADING_BREAKS_INSIDE_PARAGRAPH_PATTERN = /<p>(?:\s*<br\s*\/?>)+/gi;
const PARAGRAPH_INSIDE_LIST_ITEM_PATTERN =
  /<li>\s*<p>([\s\S]*?)<\/p>\s*<\/li>/gi;

export interface HtmlNormalizeOptions {
  /**
   * Removes paragraphs that contain only whitespace, `&nbsp;`, or `<br>`.
   * Enabled by default because CMS blank paragraphs should not affect layout.
   */
  stripEmptyParagraphs?: boolean;
}

/**
 * Re-applies a cleanup transform until the HTML stops changing. This is useful
 * for nested empty wrappers where one cleanup pass reveals another empty tag.
 *
 * ```ts
 * runUntilStable('<div><span></span></div>', (value) =>
 *   value.replace(/<([a-z][^/\s<>]*)>\s*<\/\1>/gi, ''),
 * );
 * // ''
 * ```
 */
const runUntilStable = (
  value: string,
  transform: (current: string) => string
): string => {
  let current = value;
  let previous: string;

  do {
    previous = current;
    current = transform(current);
  } while (current !== previous);

  return current;
};

/**
 * Wraps Thai phone-number text with a `tel:` link, unless the phone number is
 * already inside an anchor tag.
 *
 * ```ts
 * wrapTelLink('ติดต่อ 02-123-4567');
 * // 'ติดต่อ <a href="tel:021234567">02-123-4567</a>'
 *
 * wrapTelLink('<a href="tel:021234567">02-123-4567</a>');
 * // '<a href="tel:021234567">02-123-4567</a>'
 * ```
 */
const wrapTelLink = (html: string) => {
  return html.replace(
    TEL_LINK_PATTERN,
    (match: string, _g1: string, offset: number, full: string) => {
      const lastOpenA = full.lastIndexOf('<a', offset);
      const lastCloseA = full.lastIndexOf('</a>', offset);

      if (lastOpenA !== -1 && lastCloseA < lastOpenA) {
        return match;
      }

      const telHref = match.replace(/\D/g, '');
      return `<a href="tel:${telHref}">${match}</a>`;
    }
  );
};

/**
 * Removes empty paragraphs that contain only whitespace, non-breaking spaces,
 * or break tags.
 *
 * ```ts
 * removeEmptyParagraphs('<p><br></p><p>รายละเอียด</p>');
 * // '<p>รายละเอียด</p>'
 *
 * removeEmptyParagraphs('<p class="lead">&nbsp;</p><p>สมัครบริการ</p>');
 * // '<p>สมัครบริการ</p>'
 * ```
 */
const removeEmptyParagraphs = (html: string) =>
  html.replace(EMPTY_PARAGRAPH_PATTERN, '');

/**
 * Removes empty HTML tags without attributes recursively. Empty tags with
 * attributes can be parser markers, such as privacy policy component slots.
 *
 * ```ts
 * removeEmptyTags('<div><span></span></div><p>เนื้อหา</p>');
 * // '<p>เนื้อหา</p>'
 *
 * removeEmptyTags('<strong></strong><span>ยังอยู่</span>');
 * // '<span>ยังอยู่</span>'
 *
 * removeEmptyTags('<div data-component="contacts"></div>');
 * // '<div data-component="contacts"></div>'
 * ```
 */
const removeEmptyTags = (value: string): string => {
  return runUntilStable(value, (current) =>
    current.replace(EMPTY_TAG_WITHOUT_ATTRIBUTES_PATTERN, '')
  );
};

/**
 * Removes `<br>` tags from the very beginning of the HTML string only.
 *
 * ```ts
 * trimLeadingBreaks('<br><br><p>เนื้อหา</p>');
 * // '<p>เนื้อหา</p>'
 *
 * trimLeadingBreaks('<p>บรรทัดแรก</p><br><p>บรรทัดสอง</p>');
 * // '<p>บรรทัดแรก</p><br><p>บรรทัดสอง</p>'
 * ```
 */
const trimLeadingBreaks = (value: string): string =>
  value.replace(LEADING_BREAKS_PATTERN, '');

/**
 * Cleans common CMS rich-text structure noise, especially paragraphs that start
 * with break tags and paragraphs that contain only break tags.
 *
 * ```ts
 * cleanCmsStructure('<p><br>เนื้อหา</p><p><br></p>');
 * // '<p>เนื้อหา</p>'
 *
 * cleanCmsStructure('<p><br></p><p>รายละเอียด</p>');
 * // '<p>รายละเอียด</p>'
 * ```
 */
const cleanCmsStructure = (value: string): string => {
  return runUntilStable(value, (current) =>
    removeEmptyTags(
      current.replace(LEADING_BREAKS_INSIDE_PARAGRAPH_PATTERN, '<p>')
    )
  );
};

/**
 * Removes paragraph wrappers inside list items so CMS lists render as normal
 * `<li>` content.
 *
 * ```ts
 * removeParagraphInsideListItem('<ul><li><p>เอกสารประกอบ</p></li></ul>');
 * // '<ul><li>เอกสารประกอบ</li></ul>'
 * ```
 */
const removeParagraphInsideListItem = (html: string): string => {
  return html.replace(PARAGRAPH_INSIDE_LIST_ITEM_PATTERN, '<li>$1</li>');
};

/**
 * Applies light whitespace cleanup without changing the HTML structure.
 *
 * ```ts
 * import { normalizeHtmlWhitespace } from '@/utils/format/html';
 *
 * normalizeHtmlWhitespace('  ติดต่อ 02-123-4567&nbsp;  ');
 * // 'ติดต่อ <a href="tel:021234567">02-123-4567</a>'
 *
 * normalizeHtmlWhitespace('สมัคร&nbsp;  วันนี้');
 * // 'สมัคร วันนี้'
 *
 * normalizeHtmlWhitespace('<p><br></p><p>รายละเอียด</p>');
 * // '<p>รายละเอียด</p>'
 *
 * normalizeHtmlWhitespace('<p><br></p><p>รายละเอียด</p>', {
 *   stripEmptyParagraphs: false,
 * });
 * // '<p><br></p><p>รายละเอียด</p>'
 * ```
 */
export const normalizeHtmlWhitespace = (
  html: string,
  options: HtmlNormalizeOptions = {}
): string => {
  const { stripEmptyParagraphs = true } = options;

  const withoutEmptyParagraphs = stripEmptyParagraphs
    ? removeEmptyParagraphs(html)
    : html;
  const cleaned = withoutEmptyParagraphs
    .replace(/&nbsp;/g, ' ')
    .replace(/ {2,}/g, ' ')
    .trim();

  return wrapTelLink(cleaned);
};

/**
 * Normalizes HTML generated by CMS rich text fields before it is sanitized.
 * This removes common wrapper noise while preserving meaningful content.
 *
 * ```ts
 * import { normalizeCmsHtml } from '@/utils/format/html';
 *
 * normalizeCmsHtml('<br><ul><li><p>เอกสารประกอบ</p></li></ul>');
 * // '<ul><li>เอกสารประกอบ</li></ul>'
 *
 * normalizeCmsHtml('<p><br></p><p>ติดต่อ 02-123-4567</p>');
 * // '<p>ติดต่อ <a href="tel:021234567">02-123-4567</a></p>'
 * ```
 */
export const normalizeCmsHtml = (
  html: string,
  options: HtmlNormalizeOptions = {}
): string => {
  let normalized = html;

  normalized = trimLeadingBreaks(normalized);
  normalized = cleanCmsStructure(normalized);
  normalized = removeParagraphInsideListItem(normalized);

  return normalizeHtmlWhitespace(normalized, options);
};
